import { useCallback } from 'react';
import { PlaceField } from '../../shared/place_search/place_field.js';
import type { PlaceFieldValue } from '../../shared/place_search/place_field_trigger.js';
import type { PlaceSuggestion } from '../../shared/place_search/place_panel.js';
import type { FlightPlannerCopy } from './planner_copy.js';
import type { SearchSelectOption, SearchSelectSection } from './place_option.js';
import { AirplaneLandingIcon, AirplaneTakeoffIcon } from '@phosphor-icons/react';

/** O glifo de cada ponta: a origem DECOLA, o destino POUSA. Um pino igual nas
 *  duas diria "lugar" duas vezes e deixaria a direção só pra seta do meio —
 *  decolagem e pouso são um par assimétrico, e cada ponta passa a dizer o que
 *  acontece nela mesmo quando a seta sai do enquadramento (largura de bolso). */
const ENDPOINT_ICON = {
  origin: AirplaneTakeoffIcon,
  destination: AirplaneLandingIcon,
} as const;

/** Só um IATA PURO entra na derivação de rótulo: uma perna multi semeada com texto
 *  livre ("São Paulo") ecoaria pelo cityByAirport e viraria "SÃO PAULO SÃO PAULO". */
const BARE_IATA = /^[A-Za-z]{3}$/;

/** A sugestão do painel COM o eco que o wire espera: `echoLabel` é o `label` da
 *  opção original (`IATA cidade`), que o `onChange` devolve pro bloco — o
 *  painel mostra as duas linhas, o eco segue sendo a linha de sempre. */
type AirportPlace = PlaceSuggestion & { echoLabel: string };

/** Opção do combobox → linha do painel compartilhado. A receita das duas linhas
 *  já mora no `detail` (#2456): código + título fortes, `caption` (ou o `hint`
 *  antigo) fraco, glifo do que a linha entrega. */
function asPlace(o: SearchSelectOption, groupLabel?: string): AirportPlace {
  const description = o.detail?.caption ?? o.detail?.hint;
  return {
    id: o.value,
    label: o.detail?.title ?? o.label,
    echoLabel: o.label,
    ...(o.detail?.code ? { code: o.detail.code } : {}),
    ...(description ? { description } : {}),
    ...(o.detail?.icon ? { icon: o.detail.icon } : {}),
    ...(groupLabel ? { groupLabel } : {}),
  };
}

/** Uma linha por IATA, a 1ª ocorrência vence (#4626): o `id` da linha é o
 *  `value` do item no cmdk, e dois itens com o mesmo `value` acendem juntos no
 *  hover e deixam o Enter ambíguo. O fornecedor já mandou o mesmo aeroporto duas
 *  vezes no histórico (uma com o país errado) — a ordem dele segue valendo. */
function uniquePlaces(places: AirportPlace[]): AirportPlace[] {
  const seen = new Set<string>();
  return places.filter((p) => {
    const key = p.id.trim().toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** O que a pílula mostra: código em peso médio + cidade em muted (`GRU São
 *  Paulo`). O rótulo semeado/ecoado é `IATA cidade` numa string só — o código na
 *  frente se separa de volta; rótulo fora desse formato fica inteiro no título. */
function pillValue(
  value: string,
  selectedLabel: string | undefined,
  derive: () => string,
): PlaceFieldValue | undefined {
  const code = value.trim().toUpperCase();
  const label = selectedLabel || (BARE_IATA.test(value) ? derive() : value);
  if (!label) return undefined;
  if (label.toUpperCase().startsWith(`${code} `)) {
    return { code, title: label.slice(code.length + 1) };
  }
  return label === code ? { title: code } : { title: label };
}

/** Campo DE/PARA: o `PlaceField` do DS (pílula + painel compartilhado) com a
 *  BUSCA REMOTA de aeroportos do port flightPlanner. O wire exige IATA — o valor
 *  selecionado é o IATA; `selectedLabel` mostra o rótulo do bloco quando a rota
 *  vem pré-semeada. Pick-based: cada escolha já é o commit. Compartilhado entre
 *  o par único (flight_planner_card) e cada trecho do modo multi
 *  (multi_leg_builder) — não duplicar. */
export function RouteField({
  label,
  value,
  endpoint,
  selectedLabel,
  disabled,
  placeholder,
  promptText,
  emptyText,
  onSearch,
  onChange,
  copy,
  airportLabel,
  hideLabel = false,
  sections,
  sheet = false,
  wanted = false,
  advance = false,
  onOpenChange,
}: {
  readonly label: string;
  readonly value: string;
  /** Qual ponta do trecho o campo representa — só o glifo depende disso. */
  readonly endpoint: keyof typeof ENDPOINT_ICON;
  readonly selectedLabel?: string;
  readonly disabled: boolean;
  readonly placeholder: string;
  readonly promptText: string;
  readonly emptyText: string;
  readonly onSearch: (term: string) => Promise<SearchSelectOption[]>;
  readonly onChange: (v: string, label: string) => void;
  readonly copy: FlightPlannerCopy;
  /** Ver `PlannerFormProps.airportLabel`: o rótulo de um IATA cru na pílula. */
  readonly airportLabel: ((iata: string) => string) | undefined;
  /** Rótulo visível calado (linhas 2+ do multi, #2136) — a coluna já foi
   *  nomeada pela primeira; o nome acessível fica no campo. */
  readonly hideLabel?: boolean;
  /** Sugestões pré-digitação desta ponta (ex.: "Recentes").
   *  Ausente ⇒ o campo aberto pede "Digite para buscar", como antes. */
  readonly sections?: SearchSelectSection[];
  /** A lista abre numa FOLHA de tela cheia (#2653) em vez do popover — o palco
   *  estreito do layout aberto. Ausente ⇒ popover. */
  readonly sheet?: boolean;
  /** O campo PEDE A VEZ — o anel de pendente do preenchimento guiado da
   *  página (`data-wanted` da pílula do DS, a mesma cor do `.is-wanted` do
   *  multi). */
  readonly wanted?: boolean;
  /** Abre como CONTINUAÇÃO do gesto que respondeu o campo anterior (o
   *  preenchimento guiado): a borda falso→verdadeiro abre a lista, e a pessoa
   *  segue podendo fechar sem o campo reabrir — é o `defaultOpen` do
   *  `PlaceField`. Quem pede avisa-se pelo `onOpenChange(false)` de que a
   *  abertura foi consumida. */
  readonly advance?: boolean;
  /** A lista abriu/fechou (por gesto, pick ou `advance`). */
  readonly onOpenChange?: (open: boolean) => void;
}) {
  const Glyph = ENDPOINT_ICON[endpoint];
  // Bloco semeado pelo agents chega SÓ com os códigos (route sem originLabel) — sem
  // isto o campo mostrava o IATA cru ("GRU", e com o #2429 também "SAO") até a
  // primeira interação. O host deriva pela MESMA convenção das opções
  // (`airportLabel`): cidade do domain; metro com o sufixo "todos os aeroportos".
  // Client-side de propósito: o builder roda no backend, sem a locale do viewer.
  // Sem o host, ou código fora do cadastro, degrada pro próprio código (o que o
  // campo já mostrava).
  const shown = pillValue(
    value,
    selectedLabel,
    () => airportLabel?.(value) ?? value.trim().toUpperCase(),
  );
  const zeroQuery =
    sections && uniquePlaces(sections.flatMap((s) => s.options.map((o) => asPlace(o, s.title))));
  // Estável por cima do `onSearch` estável do host — é dep do effect de busca
  // do PlaceField; uma arrow inline re-dispararia a busca a cada render.
  const searchPlaces = useCallback(
    (term: string) => onSearch(term).then((os) => uniquePlaces(os.map((o) => asPlace(o)))),
    [onSearch],
  );
  return (
    // `data-endpoint` no embrulho: é por ele que a pill segmentada do explorer
    // (#2129) sabe QUAL campo focar/abrir ao chegar por "origem" ou "destino".
    // `genui2` porque o card de voo não vive sob a raiz do tema do DS — sem a
    // classe a pílula herdaria os tokens do CSS legado do host. A coluna é a
    // MESMA anatomia `.fpl-field` das vizinhas (rótulo em cima, controle
    // embaixo); `fpl-field-place` carrega o flex da fileira (ver o CSS).
    <div className="genui2 fpl-field fpl-field-place" data-endpoint={endpoint}>
      {!hideLabel && <span className="fpl-flabel">{label}</span>}
      <PlaceField<AirportPlace>
        label={label}
        {...(shown ? { value: shown } : {})}
        emptyText={placeholder}
        icon={<Glyph size={14} />}
        disabled={disabled}
        copy={{
          label,
          promptLabel: promptText,
          emptyLabel: emptyText,
          // A BUSCA falhando tem cópia própria (#2566): degradar pra frase do
          // vazio fazia uma indisponibilidade do fornecedor de praças ler como
          // "esse aeroporto não existe". Sai daqui e não de uma prop porque as
          // DUAS pontas e todo trecho do multi passam por este mesmo campo.
          errorLabel: copy.airportsSearchFailed,
          // Uma queda de segundos (#4265) não pode exigir apagar e redigitar.
          retryLabel: copy.airportsSearchRetry,
        }}
        {...(zeroQuery && zeroQuery.length > 0 ? { zeroQuery } : {})}
        onSearch={searchPlaces}
        onPick={(place) => {
          onChange(place.id, place.echoLabel);
        }}
        {...(sheet ? { sheet: { backLabel: copy.back } } : {})}
        wanted={wanted}
        defaultOpen={advance}
        {...(onOpenChange ? { onOpenChange } : {})}
      />
    </div>
  );
}
